"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { useCallback } from "react";
import { SESSION_IDENTITY_QUERY_KEY } from "@/hooks/use-auth-state";
import { SHELL_PROFILE_QUERY_KEY } from "@/hooks/use-shell-profile-data";

/** The profile card the shell and the profile screens share. */
const CURRENT_PROFILE_QUERY_KEY = ["profile", "current"] as const;

/**
 * Adopt a session that appeared WITHOUT a document load.
 *
 * Web OAuth never needed this: `window.location.assign` throws the whole
 * document away, so every cache — React Query, the client router's RSC payloads,
 * the server components themselves — is rebuilt with the new cookie attached.
 *
 * Three sign-ins do not get that reset, and each one that forgets to do this by
 * hand looks identical to "sign-in silently failed":
 *
 *   native Google / Apple — the ID-token exchange is a fetch inside the same
 *   runtime, and
 *
 *   phone OTP — two fetches, on every platform. Nothing navigates at all, which
 *   is the whole point of it, and also why it is the easiest one to get wrong.
 *
 * Two caches have to be told, because two different things read them:
 *
 *   React Query holds `useAuthState`'s identity for 60s, so a page that gates on
 *   `authStatus` keeps reporting `unauthenticated` long after the cookie exists.
 *
 *   The client router holds the RSC payloads it fetched while signed out —
 *   `staleTimes.dynamic` is 30s, so a destination prefetched moments ago is
 *   eligible for reuse — and a page whose server component resolved
 *   `sessionUser: undefined` keeps rendering its signed-out branch.
 *
 * Call this once the session exists, before any navigation.
 *
 * **Not awaited, deliberately.** `invalidateQueries` marks the entries stale
 * synchronously and only the *refetch* is async, so awaiting it buys nothing
 * except a server round-trip of dead time between the tap and the screen
 * changing. There is nothing stale to flash in the meantime either: a guest
 * never populated the two profile queries, and a briefly-still-`unauthenticated`
 * `authStatus` triggers nothing on its own.
 */
export function useSessionHandoff() {
  const queryClient = useQueryClient();
  const router = useRouter();

  return useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: SESSION_IDENTITY_QUERY_KEY,
    });
    void queryClient.invalidateQueries({ queryKey: SHELL_PROFILE_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: CURRENT_PROFILE_QUERY_KEY });
    router.refresh();
  }, [queryClient, router]);
}
